import { useState } from "react";
import { MoreHorizontal, Eye, Edit3, Trash2, Copy, Archive, ArchiveRestore, ListChecks, UserPlus, UserMinus, MailCheck, Database, User, ExternalLink, ScrollText, UserCog } from "lucide-react";
import { isItemArchived } from "@/utils/itemArchive";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import type { ActionDefinition } from "../api/actionsApi";
import type { ItemPermissionAction } from "../acl/buildItemPermissionBulkRequests";

type ActionMenuProps = {
    item: any,
    onDelete: any,
    onDetails: any,
    onEdit: any,
    onDuplicate?: (item: any) => void,
    onArchive?: (item: any) => void,
    archiveDisabled?: boolean,
    onRestore?: (item: any) => void,
    restoreDisabled?: boolean,
    actions?: ActionDefinition[],
    onRunAction?: (action: ActionDefinition, item: any) => void,
    selectedRowsCount?: number,
    permissions?: {
        canRead?: boolean,
        canUpdate?: boolean,
        canDelete?: boolean,
        canDelegate?: boolean,
    },
    /** When an action is pending, its menu entry stays hidden until ACL resolves. */
    isItemPermissionPending?: (action: ItemPermissionAction) => boolean,
    customTrigger?: React.ReactNode,
    onDelegateOwner?: () => void,
    onRemoveDelegatedOwner?: () => void,
    showRemoveDelegatedOwner?: boolean,
    /** When set, overrides visibility of delegate actions (e.g. bulk row vs ACL). */
    delegateActionsVisible?: boolean,
    /** Exactly-two form submissions, same submitter; bulk toolbar only. */
    showPickAndInform?: boolean,
    onPickAndInform?: () => void,
    /** Admin: open Mongo collections inspector with this collection and uid filter. */
    showInMongo?: boolean,
    onShowInMongo?: () => void,
    /** Row came from a public form submission (`_formUid`). */
    showFormSubmissionActions?: boolean,
    onFormSubmissionShowUser?: () => void,
    onFormSubmissionShowForm?: () => void,
    onFormSubmissionShowLogs?: () => void,
    onFormSubmissionImpersonateUser?: () => void,
    formSubmissionImpersonateDisabled?: boolean,
    formSubmissionImpersonateLoading?: boolean,
};

const ActionMenu = ({
    item,
    onDelete,
    onDetails,
    onEdit,
    onDuplicate,
    onArchive,
    archiveDisabled = false,
    onRestore,
    restoreDisabled = false,
    actions = [],
    onRunAction,
    selectedRowsCount = 0,
    permissions,
    isItemPermissionPending = () => false,
    customTrigger,
    onDelegateOwner,
    onRemoveDelegatedOwner,
    showRemoveDelegatedOwner = false,
    delegateActionsVisible,
    showPickAndInform = false,
    onPickAndInform,
    showInMongo = false,
    onShowInMongo,
    showFormSubmissionActions = false,
    onFormSubmissionShowUser,
    onFormSubmissionShowForm,
    onFormSubmissionShowLogs,
    onFormSubmissionImpersonateUser,
    formSubmissionImpersonateDisabled = false,
    formSubmissionImpersonateLoading = false,
}: ActionMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const canRead = Boolean(permissions?.canRead);
    const canUpdate = Boolean(permissions?.canUpdate);
    const canDelete = Boolean(permissions?.canDelete);
    const delegateVisible =
        delegateActionsVisible !== undefined ? delegateActionsVisible : Boolean(permissions?.canDelegate);
    const readPending = isItemPermissionPending("read");
    const updatePending = isItemPermissionPending("update");
    const deletePending = isItemPermissionPending("delete");
    const delegatePending = isItemPermissionPending("delegate");
    const showDetails = !readPending && canRead;
    const showEdit = !updatePending && canUpdate;
    const showDelete = !deletePending && canDelete;
    const showDelegate =
        !delegatePending && Boolean(delegateVisible && onDelegateOwner && selectedRowsCount <= 1);
    const showRemoveOwner = Boolean(
        !delegatePending &&
        showRemoveDelegatedOwner &&
        delegateVisible &&
        onRemoveDelegatedOwner &&
        selectedRowsCount <= 1
    );
    const itemArchived = isItemArchived(item);
    const isBulkMulti = selectedRowsCount > 1;
    const showArchiveInMenu =
        Boolean(onArchive) && showEdit && (isBulkMulti ? !archiveDisabled : !itemArchived);
    const showRestoreInMenu =
        Boolean(onRestore) && showEdit && (isBulkMulti ? !restoreDisabled : itemArchived);
    const showDuplicate = showEdit && Boolean(onDuplicate);
    const hasUserActions = showEdit && actions.length > 0;

    const handleAction = (action: string, item: any) => {
        switch (action) {
            case 'delete':
                onDelete(item);
                break;
            case 'details':
                onDetails(item);
                break;
            case 'edit':
                onEdit(item);
                break;
            case 'duplicate':
                onDuplicate?.(item);
                break;
            case 'archive':
                onArchive?.(item);
                break;
            case 'restore':
                onRestore?.(item);
                break;
        }
        setIsOpen(false);
    };

    const handleUserAction = (action: ActionDefinition) => {
        if (onRunAction) {
            onRunAction(action, item);
        }
        setIsOpen(false);
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    className={`action-menu-trigger hover:bg-gray-100 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-offset-gray-900 transition-all duration-200 rounded-md ${customTrigger ? 'px-3 py-2 h-auto' : 'h-8 w-8 p-0'}`}
                    aria-label="Open action menu"
                >
                    {customTrigger ?? <MoreHorizontal className="h-4 w-4 text-gray-600 dark:text-gray-300" />}
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                className="action-menu-content w-52 bg-popover text-popover-foreground border border-gray-200 dark:border-gray-600 shadow-xl rounded-xl p-2 backdrop-blur-sm"
                sideOffset={8}
            >
                <DropdownMenuLabel className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-gray-100 dark:border-gray-700 mb-1">
                    Actions
                </DropdownMenuLabel>

                {showDetails ? (
                    <DropdownMenuItem
                        onClick={() => handleAction('details', item)}
                        className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group hover:bg-blue-50 dark:hover:bg-blue-950/50 hover:text-blue-900 dark:hover:text-blue-100 cursor-pointer"
                    >
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-950/80 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/60 transition-colors">
                            <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
                        </div>
                        <div className="flex-1">
                            <span className="font-medium">View Details</span>
                            <div className="text-xs text-muted-foreground">See full information</div>
                        </div>
                    </DropdownMenuItem>
                ) : null}

                {showFormSubmissionActions &&
                    selectedRowsCount <= 1 &&
                    ((onFormSubmissionShowUser &&
                        onFormSubmissionShowForm &&
                        onFormSubmissionShowLogs) ||
                        onFormSubmissionImpersonateUser) && (
                        <>
                            <DropdownMenuSeparator className="my-2 border-gray-100 dark:border-gray-700" />
                            <DropdownMenuLabel className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                Form submission
                            </DropdownMenuLabel>
                            {onFormSubmissionImpersonateUser ? (
                                <DropdownMenuItem
                                    onClick={() => {
                                        onFormSubmissionImpersonateUser();
                                        setIsOpen(false);
                                    }}
                                    disabled={formSubmissionImpersonateDisabled || formSubmissionImpersonateLoading}
                                    className={`action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group ${formSubmissionImpersonateDisabled || formSubmissionImpersonateLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-violet-50 dark:hover:bg-violet-950/40 hover:text-violet-950 dark:hover:text-violet-100 cursor-pointer'}`}
                                >
                                    <div className="w-8 h-8 bg-violet-100 dark:bg-violet-950/80 rounded-lg flex items-center justify-center group-hover:bg-violet-200 dark:group-hover:bg-violet-900/60 transition-colors">
                                        <UserCog className="h-4 w-4 text-violet-800 dark:text-violet-300 group-hover:text-violet-900 dark:group-hover:text-violet-200" />
                                    </div>
                                    <div className="flex-1">
                                        <span className="font-medium">
                                            {formSubmissionImpersonateLoading ? 'Impersonating...' : 'Impersonate user'}
                                        </span>
                                        <div className="text-xs text-muted-foreground">Log in as the submitter</div>
                                    </div>
                                </DropdownMenuItem>
                            ) : null}
                            {onFormSubmissionShowUser ? (
                                <DropdownMenuItem
                                    onClick={() => {
                                        onFormSubmissionShowUser();
                                        setIsOpen(false);
                                    }}
                                    className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group hover:bg-cyan-50 dark:hover:bg-cyan-950/40 hover:text-cyan-950 dark:hover:text-cyan-100 cursor-pointer"
                                >
                                    <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-950/80 rounded-lg flex items-center justify-center group-hover:bg-cyan-200 dark:group-hover:bg-cyan-900/60 transition-colors">
                                        <User className="h-4 w-4 text-cyan-800 dark:text-cyan-300 group-hover:text-cyan-900 dark:group-hover:text-cyan-200" />
                                    </div>
                                    <div className="flex-1">
                                        <span className="font-medium">Show user</span>
                                        <div className="text-xs text-muted-foreground">Submitter and ownership fields</div>
                                    </div>
                                </DropdownMenuItem>
                            ) : null}
                            {onFormSubmissionShowForm ? (
                                <DropdownMenuItem
                                    onClick={() => {
                                        onFormSubmissionShowForm();
                                        setIsOpen(false);
                                    }}
                                    className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group hover:bg-indigo-50 dark:hover:bg-indigo-950/40 hover:text-indigo-950 dark:hover:text-indigo-100 cursor-pointer"
                                >
                                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-950/80 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/60 transition-colors">
                                        <ExternalLink className="h-4 w-4 text-indigo-700 dark:text-indigo-300 group-hover:text-indigo-800 dark:group-hover:text-indigo-200" />
                                    </div>
                                    <div className="flex-1">
                                        <span className="font-medium">Show form</span>
                                        <div className="text-xs text-muted-foreground">Open public form in a new tab</div>
                                    </div>
                                </DropdownMenuItem>
                            ) : null}
                            {onFormSubmissionShowLogs ? (
                                <DropdownMenuItem
                                    onClick={() => {
                                        onFormSubmissionShowLogs();
                                        setIsOpen(false);
                                    }}
                                    className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group hover:bg-stone-50 dark:hover:bg-stone-900/80 hover:text-stone-900 dark:hover:text-stone-100 cursor-pointer"
                                >
                                    <div className="w-8 h-8 bg-stone-100 dark:bg-stone-800 rounded-lg flex items-center justify-center group-hover:bg-stone-200 dark:group-hover:bg-stone-700 transition-colors">
                                        <ScrollText className="h-4 w-4 text-stone-700 dark:text-stone-300 group-hover:text-stone-800 dark:group-hover:text-stone-200" />
                                    </div>
                                    <div className="flex-1">
                                        <span className="font-medium">Show logs</span>
                                        <div className="text-xs text-muted-foreground">Admin email and activity logs</div>
                                    </div>
                                </DropdownMenuItem>
                            ) : null}
                        </>
                    )}

                {showEdit && !itemArchived ? (
                    <DropdownMenuItem
                        onClick={() => handleAction('edit', item)}
                        className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group hover:bg-green-50 dark:hover:bg-green-950/50 hover:text-green-900 dark:hover:text-green-100 cursor-pointer"
                    >
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-950/80 rounded-lg flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-900/60 transition-colors">
                            <Edit3 className="h-4 w-4 text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300" />
                        </div>
                        <div className="flex-1">
                            <span className="font-medium">Edit {selectedRowsCount > 1 ? 'Items' : 'Item'}</span>
                            <div className="text-xs text-muted-foreground">Modify this entry</div>
                        </div>
                    </DropdownMenuItem>
                ) : null}

                {showDelegate ? (
                    <DropdownMenuItem
                        onClick={() => {
                            onDelegateOwner?.();
                            setIsOpen(false);
                        }}
                        className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group hover:bg-violet-50 dark:hover:bg-violet-950/50 hover:text-violet-900 dark:hover:text-violet-100 cursor-pointer"
                    >
                        <div className="w-8 h-8 bg-violet-100 dark:bg-violet-950/80 rounded-lg flex items-center justify-center group-hover:bg-violet-200 dark:group-hover:bg-violet-900/60 transition-colors">
                            <UserPlus className="h-4 w-4 text-violet-600 dark:text-violet-400 group-hover:text-violet-700 dark:group-hover:text-violet-300" />
                        </div>
                        <div className="flex-1">
                            <span className="font-medium">Delegate owner</span>
                            <div className="text-xs text-muted-foreground">Transfer owner rights to another user</div>
                        </div>
                    </DropdownMenuItem>
                ) : null}

                {showRemoveOwner ? (
                    <DropdownMenuItem
                        onClick={() => {
                            onRemoveDelegatedOwner?.();
                            setIsOpen(false);
                        }}
                        className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group hover:bg-slate-50 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-slate-100 cursor-pointer"
                    >
                        <div className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center group-hover:bg-slate-200 dark:group-hover:bg-slate-700 transition-colors">
                            <UserMinus className="h-4 w-4 text-slate-600 dark:text-slate-300 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
                        </div>
                        <div className="flex-1">
                            <span className="font-medium">Remove delegated owner</span>
                            <div className="text-xs text-muted-foreground">Clear delegation; creator regains ownership</div>
                        </div>
                    </DropdownMenuItem>
                ) : null}

                {showPickAndInform && onPickAndInform && selectedRowsCount >= 2 && (
                    <DropdownMenuItem
                        onClick={() => {
                            onPickAndInform();
                            setIsOpen(false);
                        }}
                        className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm hover:bg-sky-50 dark:hover:bg-sky-950/50 hover:text-sky-900 dark:hover:text-sky-100 rounded-lg cursor-pointer transition-all duration-200 group"
                    >
                        <div className="w-8 h-8 bg-sky-100 dark:bg-sky-950/80 rounded-lg flex items-center justify-center group-hover:bg-sky-200 dark:group-hover:bg-sky-900/60 transition-colors">
                            <MailCheck className="h-4 w-4 text-sky-600 dark:text-sky-400 group-hover:text-sky-700 dark:group-hover:text-sky-300" />
                        </div>
                        <div className="flex-1">
                            <span className="font-medium">Pick and inform</span>
                            <div className="text-xs text-muted-foreground">Keep one submission, archive the rest, email submitter</div>
                        </div>
                    </DropdownMenuItem>
                )}

                {showInMongo && onShowInMongo && selectedRowsCount <= 1 && (
                    <DropdownMenuItem
                        onClick={() => {
                            onShowInMongo();
                            setIsOpen(false);
                        }}
                        className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:text-amber-950 dark:hover:text-amber-100 rounded-lg cursor-pointer transition-all duration-200 group"
                    >
                        <div className="w-8 h-8 bg-amber-100 dark:bg-amber-950/80 rounded-lg flex items-center justify-center group-hover:bg-amber-200 dark:group-hover:bg-amber-900/60 transition-colors">
                            <Database className="h-4 w-4 text-amber-800 dark:text-amber-300 group-hover:text-amber-900 dark:group-hover:text-amber-200" />
                        </div>
                        <div className="flex-1">
                            <span className="font-medium">Show in Mongo</span>
                            <div className="text-xs text-muted-foreground">Admin inspector: collection + uid filter</div>
                        </div>
                    </DropdownMenuItem>
                )}

                {showDuplicate ? (
                    <DropdownMenuItem
                        onClick={() => handleAction('duplicate', item)}
                        className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group hover:bg-purple-50 dark:hover:bg-purple-950/50 hover:text-purple-900 dark:hover:text-purple-100 cursor-pointer"
                    >
                        <div className="w-8 h-8 bg-purple-100 dark:bg-purple-950/80 rounded-lg flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/60 transition-colors">
                            <Copy className="h-4 w-4 text-purple-600 dark:text-purple-400 group-hover:text-purple-700 dark:group-hover:text-purple-300" />
                        </div>
                        <div className="flex-1">
                            <span className="font-medium">Duplicate</span>
                            <div className="text-xs text-muted-foreground">Create a copy</div>
                        </div>
                    </DropdownMenuItem>
                ) : null}

                {(showArchiveInMenu || showRestoreInMenu) && (
                    <>
                        <DropdownMenuSeparator className="my-2 border-gray-100 dark:border-gray-700" />
                        {showArchiveInMenu && (
                            <DropdownMenuItem
                                onClick={() => handleAction('archive', item)}
                                disabled={archiveDisabled || !onArchive}
                                className={`action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group ${archiveDisabled || !onArchive ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-50 dark:hover:bg-orange-950/50 hover:text-orange-900 dark:hover:text-orange-100 cursor-pointer'}`}
                            >
                                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-950/80 rounded-lg flex items-center justify-center group-hover:bg-orange-200 dark:group-hover:bg-orange-900/60 transition-colors">
                                    <Archive className="h-4 w-4 text-orange-600 dark:text-orange-400 group-hover:text-orange-700 dark:group-hover:text-orange-300" />
                                </div>
                                <div className="flex-1">
                                    <span className="font-medium">Archive</span>
                                    <div className="text-xs text-muted-foreground">Move to archive</div>
                                </div>
                            </DropdownMenuItem>
                        )}
                        {showRestoreInMenu && (
                            <DropdownMenuItem
                                onClick={() => handleAction('restore', item)}
                                disabled={restoreDisabled || !onRestore}
                                className={`action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 group ${restoreDisabled || !onRestore ? 'opacity-50 cursor-not-allowed' : 'hover:bg-teal-50 dark:hover:bg-teal-950/50 hover:text-teal-900 dark:hover:text-teal-100 cursor-pointer'}`}
                            >
                                <div className="w-8 h-8 bg-teal-100 dark:bg-teal-950/80 rounded-lg flex items-center justify-center group-hover:bg-teal-200 dark:group-hover:bg-teal-900/60 transition-colors">
                                    <ArchiveRestore className="h-4 w-4 text-teal-600 dark:text-teal-400 group-hover:text-teal-700 dark:group-hover:text-teal-300" />
                                </div>
                                <div className="flex-1">
                                    <span className="font-medium">Restore</span>
                                    <div className="text-xs text-muted-foreground">Return to active list</div>
                                </div>
                            </DropdownMenuItem>
                        )}
                    </>
                )}

                {showDelete ? (
                    <>
                        <DropdownMenuSeparator className="my-2 border-gray-100 dark:border-gray-700" />

                        <DropdownMenuItem
                            onClick={() => handleAction('delete', item)}
                            className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm text-red-700 dark:text-red-400 rounded-lg transition-all duration-200 group hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-900 dark:hover:text-red-300 cursor-pointer"
                        >
                            <div className="w-8 h-8 bg-red-100 dark:bg-red-950/80 rounded-lg flex items-center justify-center group-hover:bg-red-200 dark:group-hover:bg-red-900/50 transition-colors">
                                <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300" />
                            </div>
                            <div className="flex-1">
                                <span className="font-medium">Delete {selectedRowsCount > 1 ? 'Items' : 'Item'}</span>
                                <div className="text-xs text-muted-foreground">Remove permanently</div>
                            </div>
                        </DropdownMenuItem>
                    </>
                ) : null}

                {hasUserActions && (
                    <div className="space-y-1">
                        <DropdownMenuSeparator className="my-2 border-gray-100 dark:border-gray-700" />
                        <DropdownMenuLabel className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            User actions
                        </DropdownMenuLabel>
                        {actions.map((action) => (
                            <DropdownMenuItem
                                key={action.uid || action.name}
                                onClick={() => handleUserAction(action)}
                                className="action-menu-item flex items-center space-x-3 px-3 py-2.5 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-950/50 hover:text-indigo-900 dark:hover:text-indigo-100 rounded-lg cursor-pointer transition-all duration-200 group"
                            >
                                <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-950/80 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/60 transition-colors">
                                    <ListChecks className="h-4 w-4 text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300" />
                                </div>
                                <div className="flex-1">
                                    <span className="font-medium">{action.name}</span>
                                    <div className="text-xs text-muted-foreground">{action.operation?.field || "Action"}</div>
                                </div>
                            </DropdownMenuItem>
                        ))}
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ActionMenu;